import { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { projectsApi } from '@/api/projects.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column, TableRowActions } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useDebounce } from '@/hooks/useDebounce';
import { usePagination } from '@/hooks/usePagination';
import type { ProjectProject } from '@/types/projects';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function ProjectsListPage() {
  const pagination = usePagination();
  const debouncedSearch = useDebounce(pagination.search);
  const [data, setData] = useState<ProjectProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ projectCode: '', projectName: '', projectType: 'INTERNAL', budget: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await projectsApi.listProjects({ page: pagination.page, size: pagination.size, search: debouncedSearch || undefined });
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [pagination, debouncedSearch]);

  useEffect(() => { void load(); }, [load]);

  const columns: Column<ProjectProject>[] = [
    { key: 'projectCode', header: 'Code' },
    { key: 'projectName', header: 'Name' },
    { key: 'projectType', header: 'Type' },
    { key: 'lifecycleStatus', header: 'Lifecycle' },
    { key: 'budget', header: 'Budget' },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <TableRowActions>
          <Button variant="secondary" size="sm" onClick={() => void archive(row.id)} disabled={row.archived}>
            Archive
          </Button>
          <Button variant="secondary" size="sm" onClick={() => void clone(row)}>
            Clone
          </Button>
        </TableRowActions>
      ),
    },
  ];

  const archive = async (id: number) => {
    try {
      await projectsApi.archiveProject(id);
      notify.success('Project archived');
      load();
    } catch (err) {
      showApiError(err, 'Archive failed');
    }
  };

  const clone = async (project: ProjectProject) => {
    const code = `${project.projectCode}-COPY`;
    const name = `${project.projectName} (Copy)`;
    try {
      await projectsApi.cloneProject(project.id, { projectCode: code, projectName: name, includeTasks: true });
      notify.success('Project cloned');
      load();
    } catch (err) {
      showApiError(err, 'Clone failed');
    }
  };

  const create = async () => {
    try {
      await projectsApi.createProject(form);
      notify.success('Project created');
      setOpen(false);
      setForm({ projectCode: '', projectName: '', projectType: 'INTERNAL', budget: 0 });
      load();
    } catch (err) {
      showApiError(err, 'Create failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Projects"
        description="Portfolio of client and internal delivery projects"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Projects', to: '/projects' }, { label: 'List' }]}
        actions={<Button onClick={() => setOpen(true)}><AddIcon fontSize="small" /> New project</Button>}
      />
      <DataTable columns={columns} data={data} keyField="id" loading={loading}
        search={pagination.search} onSearchChange={(v) => { pagination.setSearch(v); pagination.resetPage(); }}
        page={pagination.page} totalPages={pagination.totalPages} totalElements={pagination.totalElements} onPageChange={pagination.setPage} />
      <Modal open={open} onClose={() => setOpen(false)} title="Create project" footer={<><Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={() => void create()}>Save</Button></>}>
        <div className="space-y-3">
          <Input label="Project code" value={form.projectCode} onChange={(e) => setForm({ ...form, projectCode: e.target.value })} />
          <Input label="Project name" value={form.projectName} onChange={(e) => setForm({ ...form, projectName: e.target.value })} />
          <Input label="Budget" type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: Number(e.target.value) })} />
        </div>
      </Modal>
    </div>
  );
}
